import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery } from 'react-query';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  ArrowLeftIcon,
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  CalendarDaysIcon,
  CreditCardIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

const MemberDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();

  // Données fictives
  const mockMembers = [
    { id: 'M001', memberCode: 'MEM-2023-001', name: 'Ahmed Benaissa', phone: '0551234567', email: 'ahmed.b@email.com', status: 'active', joinDate: '2023-01-15', currentSubscription: { type: 'Mensuel', startDate: '2023-07-01', endDate: '2023-08-01', price: 5000 }, photo: null, address: '15 Rue des Frères Bouadou, Alger', birthDate: '1992-05-15', gender: 'M', emergencyContact: 'Nadir Benaissa', emergencyPhone: '0661234567', notes: 'Préfère les séances du matin', medicalNotes: 'Aucun problème de santé connu' },
    { id: 'M002', memberCode: 'MEM-2023-002', name: 'Fatima Khelil', phone: '0661234567', email: 'fatima.k@email.com', status: 'active', joinDate: '2023-01-20', currentSubscription: { type: 'Trimestriel', startDate: '2023-06-01', endDate: '2023-09-01', price: 12000 }, photo: null, address: '27 Boulevard Mohamed V, Alger', birthDate: '1988-11-20', gender: 'F', emergencyContact: 'Kamel Khelil', emergencyPhone: '0771234567', notes: 'Intéressée par les cours de yoga', medicalNotes: 'Légère douleur au genou droit' },
    { id: 'M003', memberCode: 'MEM-2023-003', name: 'Karim Messaoud', phone: '0771234567', email: 'karim.m@email.com', status: 'active', joinDate: '2023-02-05', currentSubscription: { type: 'Annuel', startDate: '2023-02-05', endDate: '2024-02-05', price: 40000 }, photo: null, address: '5 Rue Didouche Mourad, Alger', birthDate: '1985-03-10', gender: 'M', emergencyContact: 'Samia Messaoud', emergencyPhone: '0551234568', notes: 'Bodybuilder expérimenté', medicalNotes: 'Aucun problème de santé' },
    { id: 'M004', memberCode: 'MEM-2023-004', name: 'Samia Benarfa', phone: '0551234568', email: 'samia.b@email.com', status: 'inactive', joinDate: '2023-02-10', currentSubscription: null, photo: null, address: '42 Avenue Ali Khodja, Constantine', birthDate: '1995-08-22', gender: 'F', emergencyContact: 'Youcef Benarfa', emergencyPhone: '0661234568', notes: 'A arrêté son abonnement pour raisons personnelles', medicalNotes: 'Asthmatique légère' },
    { id: 'M005', memberCode: 'MEM-2023-005', name: 'Mohamed Hakim', phone: '0661234568', email: 'mohamed.h@email.com', status: 'active', joinDate: '2023-03-01', currentSubscription: { type: 'Mensuel', startDate: '2023-07-01', endDate: '2023-08-01', price: 5000 }, photo: null, address: '3 Rue Ben M\'hidi, Oran', birthDate: '1990-12-15', gender: 'M', emergencyContact: 'Amina Hakim', emergencyPhone: '0771234568', notes: 'Intéressé par le coaching personnel', medicalNotes: 'Tension artérielle légèrement élevée' },
    { id: 'M006', memberCode: 'MEM-2023-006', name: 'Amina Ferhat', phone: '0771234568', email: 'amina.f@email.com', status: 'suspended', joinDate: '2023-03-15', currentSubscription: { type: 'Mensuel', startDate: '2023-07-01', endDate: '2023-08-01', price: 5000 }, photo: null, address: '17 Rue Abane Ramdane, Alger', birthDate: '1993-04-30', gender: 'F', emergencyContact: 'Samir Ferhat', emergencyPhone: '0551234569', notes: 'Suspension temporaire pour voyage', medicalNotes: 'Aucun problème de santé' }
  ];

  // Tenter de récupérer les données depuis l'API, avec fallback sur les données fictives
  const { data: member, isLoading } = useQuery(
    ['member', id],
    async () => {
      try {
        const response = await axios.get(`/members/${id}`);
        return response.data;
      } catch (error) {
        // En cas d'erreur API, utiliser les données fictives
        const mockMember = mockMembers.find(m => m.id === id);
        if (!mockMember) {
          throw new Error('Membre introuvable');
        }
        return mockMember;
      }
    },
    {
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/20 text-green-400';
      case 'suspended':
        return 'bg-yellow-500/20 text-yellow-400';
      case 'inactive':
        return 'bg-red-500/20 text-red-400';
      default:
        return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon className="h-5 w-5 text-green-400" />;
      case 'suspended':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />;
      case 'inactive':
        return <XCircleIcon className="h-5 w-5 text-red-400" />;
      default:
        return null;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'Actif';
      case 'suspended':
        return 'Suspendu';
      case 'inactive':
        return 'Inactif';
      default:
        return 'Inconnu';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-white/30"></div>
      </div>
    );
  }

  if (!member) {
    return (
      <div className="max-w-4xl mx-auto text-center space-y-4">
        <div className="text-red-400 text-xl">Membre introuvable</div>
        <button
          onClick={() => navigate('/members')}
          className="bg-blue-500 text-white px-6 py-3 rounded-xl hover:bg-blue-600 transition-colors"
        >
          Retour à la liste
        </button>
      </div>
    );
  }

  // Déterminer le type d'objet emergencyContact
  const emergencyContactIsObject = member.emergencyContact && typeof member.emergencyContact === 'object';

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/members')}
            className="p-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-white">
              Profil du Membre
            </h1>
            <p className="text-white/60 mt-2">
              Informations détaillées et historique
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Link
            to={`/members/${id}/edit`}
            className="bg-blue-500/20 text-blue-400 px-4 py-2 rounded-lg hover:bg-blue-500/30 transition-colors flex items-center space-x-2"
          >
            <PencilIcon className="h-4 w-4" />
            <span>Modifier</span>
          </Link>
          <button className="bg-red-500/20 text-red-400 px-4 py-2 rounded-lg hover:bg-red-500/30 transition-colors flex items-center space-x-2">
            <TrashIcon className="h-4 w-4" />
            <span>Supprimer</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <div className="lg:col-span-1">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 space-y-6">
            {/* Profile Photo */}
            <div className="text-center">
              <div className="w-32 h-32 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4 overflow-hidden">
                {member.photo ? (
                  <img
                    src={member.photo}
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <UserIcon className="h-16 w-16 text-white" />
                )}
              </div>
              <h2 className="text-2xl font-bold text-white">{member.name}</h2>
              <p className="text-white/60 font-mono text-sm mt-1">
                {member.memberCode}
              </p>
            </div>

            {/* Status */}
            <div className="flex items-center justify-center space-x-2">
              {getStatusIcon(member.status)}
              <span className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(member.status)}`}>
                {getStatusText(member.status)}
              </span>
            </div>

            {/* Quick Info */}
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <PhoneIcon className="h-5 w-5 text-white/50" />
                <span className="text-white">{member.phone}</span>
              </div>
              
              {member.email && (
                <div className="flex items-center space-x-3">
                  <EnvelopeIcon className="h-5 w-5 text-white/50" />
                  <span className="text-white">{member.email}</span>
                </div>
              )}

              {member.address && (
                <div className="flex items-center space-x-3">
                  <MapPinIcon className="h-5 w-5 text-white/50" />
                  <span className="text-white">{member.address}</span>
                </div>
              )}

              <div className="flex items-center space-x-3">
                <CalendarDaysIcon className="h-5 w-5 text-white/50" />
                <span className="text-white">
                  Inscrit le {new Date(member.joinDate).toLocaleDateString('fr-FR')}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Details and History */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Information */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <h3 className="text-xl font-semibold text-white mb-4">
              Informations Personnelles
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white/60 text-sm mb-1">Nom Complet</label>
                <p className="text-white font-medium">{member.name}</p>
              </div>
              
              <div>
                <label className="block text-white/60 text-sm mb-1">Téléphone</label>
                <p className="text-white font-medium">{member.phone}</p>
              </div>
              
              {member.email && (
                <div>
                  <label className="block text-white/60 text-sm mb-1">Email</label>
                  <p className="text-white font-medium">{member.email}</p>
                </div>
              )}
              
              {member.birthDate && (
                <div>
                  <label className="block text-white/60 text-sm mb-1">Date de Naissance</label>
                  <p className="text-white font-medium">
                    {new Date(member.birthDate).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              )}
              
              {member.address && (
                <div className="md:col-span-2">
                  <label className="block text-white/60 text-sm mb-1">Adresse</label>
                  <p className="text-white font-medium">{member.address}</p>
                </div>
              )}
            </div>
          </div>

          {/* Emergency Contact */}
          {((emergencyContactIsObject && (member.emergencyContact.name || member.emergencyContact.phone)) || 
             (!emergencyContactIsObject && (member.emergencyContact || member.emergencyPhone))
           ) && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <h3 className="text-xl font-semibold text-white mb-4">
                Contact d'Urgence
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {emergencyContactIsObject ? (
                  <>
                    {member.emergencyContact.name && (
                      <div>
                        <label className="block text-white/60 text-sm mb-1">Nom du Contact</label>
                        <p className="text-white font-medium">{member.emergencyContact.name}</p>
                      </div>
                    )}
                    
                    {member.emergencyContact.relation && (
                      <div>
                        <label className="block text-white/60 text-sm mb-1">Relation</label>
                        <p className="text-white font-medium">{member.emergencyContact.relation}</p>
                      </div>
                    )}
                    
                    {member.emergencyContact.phone && (
                      <div>
                        <label className="block text-white/60 text-sm mb-1">Téléphone d'Urgence</label>
                        <p className="text-white font-medium">{member.emergencyContact.phone}</p>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {member.emergencyContact && (
                      <div>
                        <label className="block text-white/60 text-sm mb-1">Nom du Contact</label>
                        <p className="text-white font-medium">{member.emergencyContact}</p>
                      </div>
                    )}
                    
                    {member.emergencyPhone && (
                      <div>
                        <label className="block text-white/60 text-sm mb-1">Téléphone d'Urgence</label>
                        <p className="text-white font-medium">{member.emergencyPhone}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Subscription */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <h3 className="text-xl font-semibold text-white mb-4">
              Abonnement Actuel
            </h3>
            {member.currentSubscription ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/60 text-sm mb-1">Type</label>
                  <p className="text-white font-medium">{member.currentSubscription.type}</p>
                </div>
                
                <div>
                  <label className="block text-white/60 text-sm mb-1">Montant</label>
                  <p className="text-white font-medium">{member.currentSubscription.price?.toLocaleString()} DA</p>
                </div>
                
                <div>
                  <label className="block text-white/60 text-sm mb-1">Date de Début</label>
                  <p className="text-white font-medium">
                    {new Date(member.currentSubscription.startDate).toLocaleDateString('fr-FR')}
                  </p>
                </div>
                
                <div>
                  <label className="block text-white/60 text-sm mb-1">Date de Fin</label>
                  <p className="text-white font-medium">
                    {new Date(member.currentSubscription.endDate).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CreditCardIcon className="h-8 w-8 text-white/30" />
                </div>
                <p className="text-white/60">Aucun abonnement actif</p>
                <Link
                  to={`/subscriptions/new?memberId=${member.id}`}
                  className="mt-4 inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-xl text-sm hover:from-blue-700 hover:to-purple-700"
                >
                  Ajouter un abonnement
                </Link>
              </div>
            )}
          </div>

          {/* Notes */}
          {member.notes && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <h3 className="text-xl font-semibold text-white mb-4">
                Notes
              </h3>
              <p className="text-white/80 whitespace-pre-wrap">{member.notes}</p>
            </div>
          )}

          {/* Medical Notes */}
          {member.medicalNotes && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <h3 className="text-xl font-semibold text-white mb-4">
                Notes Médicales
              </h3>
              <p className="text-white/80 whitespace-pre-wrap">{member.medicalNotes}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MemberDetail; 